/**
 * Pure permission-resolution logic shared by usePermissions().
 *
 * Supports both the legacy Clerk-style permission strings and the new Azure
 * dotted contract so the portal can migrate without breaking existing route
 * guards during the transition window.
 */

const RESOURCE_ALIASES: Record<string, string> = {
  person: 'users',
  internal_users: 'users',
  warehouses: 'location',
  suppliers: 'business',
  clients: 'business',
}

function trimPermission(permission: string): string {
  return permission.trim()
}

function mapResource(resource: string): string {
  return RESOURCE_ALIASES[resource] ?? resource
}

function splitPermission(permission: string): { resource: string; action: string } | null {
  const normalized = trimPermission(permission)
  if (!normalized) {
    return null
  }

  if (normalized.startsWith('org:')) {
    const parts = normalized.split(':')
    if (parts.length >= 3) {
      return { resource: mapResource(parts[1]), action: parts[2] }
    }
  }

  if (normalized.includes('.')) {
    const [resource, action] = normalized.split('.', 2)
    if (resource && action) {
      return { resource: mapResource(resource), action }
    }
  }

  if (normalized.includes(':')) {
    const [first, ...rest] = normalized.split(':')
    const resource = mapResource(rest.join(':'))
    if (first && resource) {
      return { resource, action: first }
    }
  }

  if (normalized) {
    return { resource: mapResource(normalized), action: 'read' }
  }

  return null
}

/** Convert old format (action:resource) permissions to Clerk format (org:resource:action). */
export function convertToClerkFormat(permission: string): string {
  const split = splitPermission(permission)
  if (!split) {
    return permission.trim()
  }

  return `org:${split.resource}:${split.action}`
}

/** Convert legacy Clerk-style or action:resource permissions to the Azure dotted contract. */
export function convertToAzureFormat(permission: string): string {
  const split = splitPermission(permission)
  if (!split) {
    return permission.trim()
  }

  return `${split.resource}.${split.action}`
}

function toPermissionSet(userPermissions: string[]): Set<string> {
  const values = userPermissions.flatMap((permission) => {
    const normalized = trimPermission(permission)
    return [
      normalized,
      convertToClerkFormat(normalized),
      convertToAzureFormat(normalized),
    ]
  })

  return new Set(values.filter(Boolean))
}

const BUSINESS_FALLBACKS: Record<string, string[]> = {
  'business.read': ['business.read', 'business.write', 'business.manage', 'org:business:read', 'org:business:write', 'org:business:manage'],
  'business.write': ['business.write', 'business.manage', 'org:business:write', 'org:business:manage'],
  'business.delete': ['business.delete', 'business.manage', 'org:business:delete', 'org:business:manage'],
  'location.manage': ['location.manage', 'location.write', 'location.read', 'org:location:manage', 'org:location:write', 'org:location:read'],
}

/**
 * Checks whether `userPermissions` satisfies a single required `permission`,
 * honoring the old/Clerk format conversion plus implied (:manage/:write -> :read,
 * :manage/:write -> :delete) and resource-alias (suppliers/clients -> business,
 * warehouses -> location) fallbacks.
 */
export function checkPermission(userPermissions: string[], permission: string): boolean {
  if (!userPermissions || userPermissions.length === 0) return false

  const normalized = trimPermission(permission)
  const clerkFormat = convertToClerkFormat(permission).trim()
  const azureFormat = convertToAzureFormat(permission).trim()
  const effectiveUserPermissions = toPermissionSet(userPermissions)

  if (
    effectiveUserPermissions.has(clerkFormat) ||
    effectiveUserPermissions.has(normalized) ||
    effectiveUserPermissions.has(azureFormat)
  ) {
    return true
  }

  for (const candidate of [normalized, clerkFormat, azureFormat]) {
    if (candidate.endsWith(':read') || candidate.endsWith('.read')) {
      const resource = candidate.replace(/(:read|\.read)$/, '')
      if (
        effectiveUserPermissions.has(`${resource}:manage`) ||
        effectiveUserPermissions.has(`${resource}:write`) ||
        effectiveUserPermissions.has(`${resource}.manage`) ||
        effectiveUserPermissions.has(`${resource}.write`)
      ) {
        return true
      }
    }

    if (candidate.endsWith(':delete') || candidate.endsWith('.delete')) {
      const resource = candidate.replace(/(:delete|\.delete)$/, '')
      if (
        effectiveUserPermissions.has(`${resource}:manage`) ||
        effectiveUserPermissions.has(`${resource}.manage`)
      ) {
        return true
      }
    }
  }

  const fallbacks = BUSINESS_FALLBACKS[azureFormat] || BUSINESS_FALLBACKS[clerkFormat]
  if (fallbacks && fallbacks.some((fallback) => effectiveUserPermissions.has(fallback))) {
    return true
  }

  return false
}

/** True if the user has ANY of the required permissions (matches backend `require_permission([...])` OR semantics). */
export function checkAnyPermission(userPermissions: string[], requiredPermissions: string[]): boolean {
  if (!userPermissions || userPermissions.length === 0) return false
  return requiredPermissions.some((permission) => checkPermission(userPermissions, permission))
}

/** True if the user has ALL of the required permissions. */
export function checkAllPermissions(userPermissions: string[], requiredPermissions: string[]): boolean {
  if (!userPermissions || userPermissions.length === 0) return false
  return requiredPermissions.every((permission) => checkPermission(userPermissions, permission))
}
